import LZString from 'lz-string';
import { useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { PagePaths } from '@revelio/core';

import type { PageState } from '../types';

export const useSyncPageStateToUrl = (pageState: PageState, enabled = true) => {
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();

  const newSerializedPageState = enabled
    ? LZString.compressToEncodedURIComponent(JSON.stringify(pageState))
    : '';

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (location.pathname.startsWith(`/${PagePaths.REPORT_BUILDER}`)) {
      return;
    }

    // Bail when a navigation AWAY from this page is in flight (see the matching
    // guard in useSyncFiltersToUrl). A late pageState settle here would
    // otherwise `setSearchParams({ replace: true })` against this page's bound
    // location and revert an external navigation back to this page.
    if (
      typeof window !== 'undefined' &&
      window.location.pathname !== location.pathname
    ) {
      return;
    }

    const currentSerializedPageState = new URLSearchParams(location.search).get(
      'pageState'
    );

    if (currentSerializedPageState === newSerializedPageState) {
      return;
    }

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('pageState', newSerializedPageState);
        return next;
      },
      { replace: true }
    );
  }, [
    newSerializedPageState,
    setSearchParams,
    location.pathname,
    location.search,
    enabled,
  ]);
};
